import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiGet, apiGetBlob, apiPost } from '../../lib/api';
import { type DocumentListItem, documentListItemSchema } from '../documents/documents.api';

/**
 * Documentele în așteptare: atașamentele venite pe căsuța de email.
 *
 * Un rând de aici **nu e încă un document**: poate fi un logo din semnătura expeditorului,
 * un duplicat sau un mesaj respins. Numărul din registru se alocă la atribuire, nu la
 * import (fondatorul, 2026-09-15).
 */

export const INBOX_STATUS = { pending: 1, assigned: 2, rejected: 3 } as const;

export type InboxStatus = (typeof INBOX_STATUS)[keyof typeof INBOX_STATUS];

export const INBOX_STATUS_LIST: readonly { value: InboxStatus; label: string }[] = [
  { value: INBOX_STATUS.pending, label: 'În așteptare' },
  { value: INBOX_STATUS.assigned, label: 'Atribuite' },
  { value: INBOX_STATUS.rejected, label: 'Respinse' },
];

const inboxItemSchema = z.object({
  id: z.string(),
  fromAddress: z.string(),
  fromName: z.string().nullable(),
  subject: z.string().nullable(),
  receivedAt: z.string(),
  fileName: z.string(),
  fileSize: z.number().int(),
  fileMime: z.string(),
  status: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  documentId: z.string().nullable(),
  documentNumber: z.number().int().nullable(),
  documentCompanyName: z.string().nullable(),
  customerId: z.string().nullable(),
  customerName: z.string().nullable(),
  // Partenerul, când atribuirea a mers pe el (#139). ⚠️ **Câmp separat**, nu denumirea
  // turnată în `customerName`: un ecran care citește o singură coloană „denumire" n-ar mai
  // putea spune care dintre ele e, deci ar arăta un partener ca pe un client.
  partnerId: z.string().nullable(),
  partnerName: z.string().nullable(),
  rejectReason: z.string().nullable(),
  resolvedByName: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  createdAt: z.string(),
});

export type InboxItem = z.infer<typeof inboxItemSchema>;

export const INBOX_QUERY_KEY = ['inbox'] as const;

export const inboxQueryOptions = (filters: { status: InboxStatus; page: number }) =>
  queryOptions({
    queryKey: [...INBOX_QUERY_KEY, filters] as const,
    queryFn: () =>
      apiGet(
        `/inbox?status=${filters.status}&page=${filters.page}`,
        z.object({
          items: z.array(inboxItemSchema),
          total: z.number(),
          page: z.number(),
          perPage: z.number(),
          pending: z.number(),
        }),
      ),
  });

/** Ce a adus o verificare, a jobului sau a butonului. */
const checkResultSchema = z.object({
  messages: z.number().int(),
  imported: z.number().int(),
  duplicates: z.number().int(),
});

export type InboxCheckResult = z.infer<typeof checkResultSchema>;

/**
 * Butonul **„Verifică acum”**: citește căsuța pe loc, fără să aștepte ceasul.
 *
 * Merge și cu citirea automată oprită — e chiar felul în care se probează o configurare
 * proaspătă. Ce nu face e să treacă peste blocaj: dacă jobul rulează chiar atunci, serverul
 * răspunde `409 inbox_check_in_progress`.
 */
export function checkInbox(): Promise<InboxCheckResult> {
  return apiPost('/inbox/check', {}, checkResultSchema);
}

/**
 * Fișierul, pentru previzualizare.
 *
 * Nu un `<img src>` sau un `<iframe src>` direct: cererea are nevoie de cookie-ul de
 * sesiune, iar folderul de fișiere nu e servit de niciun server web. Se ia ca blob și se
 * arată dintr-un `blob:` URL.
 */
export function fetchInboxFile(id: string): Promise<Blob> {
  return apiGetBlob(`/inbox/${id}/file`);
}

/**
 * Al cui e actul atribuit: **un client sau un partener, niciodată amândoi** (#139).
 *
 * ⚠️ **Firma doar la partener.** La un client vine de pe el — un client aparține unei
 * singure firme din grup. La un partener se alege, pentru că partenerii sunt comuni
 * grupului; luată tăcut din sesiune, ar consuma numere din registrul altei firme
 * (`architecture/tenancy.md`). Uniune discriminată, nu trei câmpuri opționale: așa ecranul
 * n-are cum să trimită jumătate din combinație.
 */
export type InboxAssignOwner =
  | { readonly kind: 'customer'; readonly customerId: string }
  | { readonly kind: 'partner'; readonly partnerId: string; readonly companyId: string };

export interface InboxAssignRequest {
  owner: InboxAssignOwner;
  documentTypeId: string;
  /** `1 nesemnat` sau `3 semnat`. `2 trimis` n-are sens pe un act venit din afară. */
  documentState: 1 | 3;
  /** `YYYY-MM-DD`. Lipsă = azi. */
  documentDate?: string;
  /** Mențiunile operatorului despre act — opționale (#137, aduse aici la #151). */
  notes?: string;
}

export function assignInboxDocument(
  id: string,
  input: InboxAssignRequest,
): Promise<DocumentListItem> {
  const { owner, ...rest } = input;

  return apiPost(
    `/inbox/${id}/assign`,
    {
      ...rest,
      // Uniunea se desface **aici**, într-un singur loc: corpul e plat pentru că `zod` cere
      // câmpuri, iar ecranul rămâne cu tipul care nu-l lasă să trimită o jumătate.
      ...(owner.kind === 'customer'
        ? { customerId: owner.customerId }
        : { partnerId: owner.partnerId, companyId: owner.companyId }),
    },
    z.object({ document: documentListItemSchema }),
  ).then((body) => body.document);
}

/**
 * Respingerea: **motivul e opțional** (#150).
 *
 * `null` nu trimite câmpul deloc, în loc să trimită un șir gol: pe server, „lipsă" și „gol"
 * ajung la același `null`, iar corpul cererii se citește ca ce s-a cerut.
 */
/**
 * Revizuirea unei respingeri: rândul se întoarce în **așteptare** (#152).
 *
 * ⚠️ Merge **doar** dintr-un rând respins. Pe unul atribuit serverul răspunde
 * `409 inbox_not_rejected`, cu mesajul care spune ce e de făcut în loc — documentul emis se
 * anulează, nu se desface de aici.
 */
export function reopenInboxDocument(id: string): Promise<InboxItem> {
  return apiPost(`/inbox/${id}/reopen`, {}, z.object({ item: inboxItemSchema })).then(
    (body) => body.item,
  );
}

export function rejectInboxDocument(id: string, reason: string | null): Promise<InboxItem> {
  return apiPost(
    `/inbox/${id}/reject`,
    reason === null ? {} : { reason },
    z.object({ item: inboxItemSchema }),
  ).then((body) => body.item);
}
